import { useState, useCallback } from "react";

import clsx from "clsx";

import InputField from "../Input-Field";
import FieldLabel from "../Field-Label";
import TagsInput from "../Tags-Input";
import { MultiSelect } from "../Select";

import { CharProps, NumberProps, DateProps } from "./types";

function InputRange<T extends number | string = number | string>(
  props: CharProps<T>
): React.ReactElement;
function InputRange(props: NumberProps): React.ReactElement;
function InputRange(props: DateProps): React.ReactElement;
function InputRange<T extends number | string = number | string>(
  props: CharProps<T> | NumberProps | DateProps
): React.ReactElement | null {
  const { type, name, label, labelTags, required, help, error } = props;
  const [focussed, setFocussed] = useState(false);

  const { onValueChange, range } = props;

  const onChangeText = useCallback(
    (values: string[]) => {
      // @ts-ignore
      onValueChange(values || []);
    },
    [onValueChange]
  );

  const onLowerChange = useCallback(
    (v) => {
      const upper = range?.[1];
      onValueChange([
        v,
        // @ts-ignore
        upper === undefined || upper === null ? null : (upper as T),
      ]);
    },
    [onValueChange, range]
  );

  const onUpperChange = useCallback(
    (v) => {
      const lower = range?.[0];
      onValueChange([
        // @ts-ignore
        lower === undefined || lower === null ? null : (lower as T),
        v,
      ]);
    },
    [onValueChange, range]
  );

  if (type === "text" || type === "char") {
    if (
      ("allowedValues" in props && props.allowedValues) ||
      ("options" in props && props.options)
    ) {
      const { placeholder } = props;
      const selectProps =
        "allowedValues" in props
          ? { allowedValues: props.allowedValues || [], placeholder }
          : { options: props.options || [], placeholder };
      return (
        <MultiSelect
          name={name}
          label={label}
          labelTags={labelTags}
          required={required}
          selected={(props.range || []) as T[]}
          // @ts-ignore
          onChange={onChangeText}
          help={help}
          error={error}
          {...selectProps}
        />
      );
    } else {
      return (
        <TagsInput
          name={name}
          label={label}
          labelTags={labelTags}
          required={required}
          selected={(props as CharProps<string>).range || []}
          onChange={onChangeText}
          help={help}
          error={error}
          // placeholder={(props as CharProps<string>).placeholder}
        />
      );
    }
  }

  const varType = type === "date" ? "date" : "numeric";
  const {
    startLabel,
    endLabel,
    startPlaceholder,
    endPlaceholder,
    hideLower,
    hideUpper,
  } = props as NumberProps | DateProps;

  const [lower, upper] = props.range || [];

  return (
    <>
      <div onFocus={() => setFocussed(true)} onBlur={() => setFocussed(false)}>
        {label && (
          <FieldLabel
            name={name}
            label={label}
            focussed={focussed}
            error={!!error}
            required={required}
            tags={labelTags}
          />
        )}
        <div className="flex">
          {!hideLower && (
            <div className="w-full ml-1 pl-1">
              {varType === "date" ? (
                <InputField
                  type={"date"}
                  value={lower === undefined || lower === null ? null : lower}
                  onChange={onLowerChange}
                  label={startLabel}
                  placeholder={startPlaceholder || "Start Date"}
                  required={required}
                />
              ) : (
                <InputField
                  type={"numeric"}
                  value={
                    lower === undefined || lower === null ? null : (lower as T)
                  }
                  onChange={onLowerChange}
                  label={startLabel}
                  placeholder={startPlaceholder || "Min Value"}
                  required={required}
                />
              )}
            </div>
          )}
          {!hideUpper && (
            <div className="w-full ml-1 pl-1">
              {varType === "date" ? (
                <InputField
                  type={"date"}
                  value={upper === undefined || upper === null ? null : upper}
                  onChange={onUpperChange}
                  label={endLabel}
                  placeholder={endPlaceholder || "End Date"}
                  required={required}
                />
              ) : (
                <InputField
                  type={"numeric"}
                  value={
                    upper === undefined || upper === null ? null : (upper as T)
                  }
                  onChange={onUpperChange}
                  label={endLabel}
                  placeholder={endPlaceholder || "Max Value"}
                  required={required}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {help && (
        <div
          className={clsx(
            "text-xs",
            !!error ? "text-error-dark" : "text-gray-700"
          )}
        >
          {help}
        </div>
      )}
      {error && <div className="text-error-dark text-xs">{error}</div>}
    </>
  );
}

export default InputRange;
